import React, { useState, useEffect } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';
import { useAuth } from '../../contexts/AuthContext';
import { database } from '../../config/backend';
import { ref, get as rtdbGet, set as rtdbSet, child } from '../../services/phpDataAdapter';
import './Consultation.css';

interface NotificationSettings {
  enabled: boolean;
  timesBefore: number[]; // em minutos: [60, 1440] = 1h antes e 1 dia antes
  sendToMobile: boolean;
  sendToWeb: boolean;
}

const DEFAULT_SETTINGS: NotificationSettings = {
  enabled: true,
  timesBefore: [60, 1440], // 1 hora e 1 dia antes
  sendToMobile: true,
  sendToWeb: true,
};

const TIME_OPTIONS = [
  { value: 15, label: '15 minutos antes' },
  { value: 30, label: '30 minutos antes' },
  { value: 60, label: '1 hora antes' },
  { value: 120, label: '2 horas antes' },
  { value: 1440, label: '1 dia antes' },
  { value: 2880, label: '2 dias antes' },
];

export const ConsultationNotificationSettings: React.FC = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<NotificationSettings>(DEFAULT_SETTINGS);

  useEffect(() => {
    if (isOpen && user) {
      loadSettings();
    }
  }, [isOpen, user]);

  const loadSettings = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const snap = await rtdbGet(child(ref(database), `users/${user.uid}/consultationNotificationSettings`));
      if (snap.exists()) {
        setSettings({ ...DEFAULT_SETTINGS, ...snap.val() });
      }
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    try {
      await rtdbSet(ref(database, `users/${user.uid}/consultationNotificationSettings`), settings);
      setIsOpen(false);
      window.alert('Configurações salvas com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
      window.alert('Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  const toggleTimeOption = (value: number) => {
    setSettings(prev => ({
      ...prev,
      timesBefore: prev.timesBefore.includes(value)
        ? prev.timesBefore.filter(t => t !== value)
        : [...prev.timesBefore, value].sort((a, b) => a - b),
    }));
  };

  return (
    <>
      <Button variant="secondary" onClick={() => setIsOpen(true)} style={{ fontSize: '13px', padding: '8px 16px' }}>
        ⚙️ Configurar Alertas
      </Button>

      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title="Configurações de Notificações de Consulta"
        footer={
          <>
            <Button variant="secondary" onClick={() => setIsOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving || settings.timesBefore.length === 0}>
              {saving ? 'Salvando...' : 'Salvar Configurações'}
            </Button>
          </>
        }
      >
        {loading ? (
          <p>Carregando configurações...</p>
        ) : (
          <div className="ns-consultation-form">
            <label className="ns-field">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span>Ativar notificações</span>
                <input
                  type="checkbox"
                  checked={settings.enabled}
                  onChange={(e) => setSettings(prev => ({ ...prev, enabled: e.target.checked }))}
                  style={{ width: 'auto', cursor: 'pointer' }}
                />
              </div>
            </label>

            {settings.enabled && (
              <>
                <div style={{ marginTop: '16px' }}>
                  <span style={{ display: 'block', fontSize: '13px', color: 'var(--page-text-muted)', marginBottom: '12px', fontWeight: 600 }}>
                    Enviar alertas:
                  </span>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {TIME_OPTIONS.map(option => (
                      <label
                        key={option.value}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '10px 12px',
                          background: settings.timesBefore.includes(option.value) ? '#eff6ff' : '#f8fafc',
                          border: `1px solid ${settings.timesBefore.includes(option.value) ? '#0ea5e9' : '#e2e8f0'}`,
                          borderRadius: '8px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                        }}
                        onClick={() => toggleTimeOption(option.value)}
                      >
                        <input
                          type="checkbox"
                          checked={settings.timesBefore.includes(option.value)}
                          onChange={() => toggleTimeOption(option.value)}
                          style={{ width: 'auto', cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: '13px', color: 'var(--page-text-primary)', flex: 1 }}>{option.label}</span>
                      </label>
                    ))}
                  </div>
                  {settings.timesBefore.length === 0 && (
                    <p style={{ fontSize: '12px', color: '#b91c1c', marginTop: '8px' }}>
                      Selecione pelo menos um horário para receber alertas
                    </p>
                  )}
                </div>

                <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e2e8f0' }}>
                  <span style={{ display: 'block', fontSize: '13px', color: 'var(--page-text-muted)', marginBottom: '12px', fontWeight: 600 }}>
                    Enviar para:
                  </span>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={settings.sendToMobile}
                        onChange={(e) => setSettings(prev => ({ ...prev, sendToMobile: e.target.checked }))}
                        style={{ width: 'auto', cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '13px', color: 'var(--page-text-primary)' }}>📱 Aplicativo Mobile</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={settings.sendToWeb}
                        onChange={(e) => setSettings(prev => ({ ...prev, sendToWeb: e.target.checked }))}
                        style={{ width: 'auto', cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '13px', color: 'var(--page-text-primary)' }}>🌐 Navegador Web</span>
                    </label>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </Modal>
    </>
  );
};

